import React from "react";
import {
  Box,
  Checkbox,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
} from "@mui/material";
import {
  ColumnDef,
  createSortedRowModel,
  rowSortingFeature,
  SortingState,
  tableFeatures,
  useTable,
} from "@tanstack/react-table";
import {useTranslation} from "react-i18next";
import {Align, CellParams, LMColumn, LMTableApi, PaginationModel, SortModel} from "./types";
import {compareValues} from "./compareValues";
import ICNSummaryFooter from "../subcomponents/Footer/ICNTableFooter";

export interface TableOptions {
  sortModel?: SortModel;
  /**
   * 'client' (default) sorts the supplied rows in-browser, matching the old
   * DataGrid whose sortingMode defaulted to client regardless of pagination.
   * 'server' disables local sorting for tables that re-fetch ordered data when
   * the sort model changes (e.g. the audit logs).
   */
  sortingMode?: "client" | "server";
  /** Render the inCareNetHF device-count totals row in the table footer. */
  summaryFooter?: boolean;
}

export type SelectionMode = "none" | "single";

export interface LMTableProps<R = any> {
  rows: R[];
  columns: LMColumn<R>[];
  options?: TableOptions;

  // selection
  selectionModel?: string[];
  selectionHandler?: (ids: string[]) => void;
  /** Defaults to 'single' when a selectionHandler is provided, else 'none'. */
  selectionMode?: SelectionMode;

  // sorting
  onSortModelChange?: (model: SortModel) => void;

  // pagination
  paginationModel?: PaginationModel;
  onPaginationModelChange?: (model: PaginationModel) => void;
  rowCount?: number;
  /** 'server' renders the given rows as-is; 'client' slices/pages locally. */
  paginationMode?: "client" | "server";
  pageSizeOptions?: number[];
  /** Show every row on one page, no pagination (replaces the old DataGrid cap workaround). */
  showFullContent?: boolean;
  hideFooter?: boolean;

  // misc
  apiRef?: React.MutableRefObject<LMTableApi<R> | null>;
  onRowClick?: (row: R) => void;
  processRowUpdate?: (newRow: R, oldRow: R) => Promise<R> | R;
  /**
   * Called when processRowUpdate rejects; the edited cell stays open for retry.
   * The rejection is logged either way.
   */
  onProcessRowUpdateError?: (error: unknown) => void;
  getRowId?: (row: R) => string;
  loading?: boolean;
  emptyMessage?: string;
  maxHeight?: number | string;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [15, 30, 50, 100];
const ALL_ROWS = -1;

/**
 * TanStack Table v9 requires every table to declare which features (and their
 * row-model factories) it uses, instead of bundling all of them by default.
 * LMTable only needs client-side sorting — pagination and filtering are done
 * entirely outside react-table (see the pagination/slicing logic below) — so
 * only rowSortingFeature + its sortedRowModel are registered here.
 *
 * Declared at module scope (not per-render) per the library's own guidance,
 * and shared by every LMTable instance since the feature set never varies.
 */
const tableFeaturesConfig = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
});
type LMTableFeatures = typeof tableFeaturesConfig;

/** Stable identity so an absent sortModel doesn't invalidate memos every render. */
const EMPTY_SORT: SortModel = [];

function toSorting(model?: SortModel): SortingState {
  return (model ?? [])
    .filter((s) => s.sort === "asc" || s.sort === "desc")
    .map((s) => ({id: s.field, desc: s.sort === "desc"}));
}

function fromSorting(state: SortingState): SortModel {
  return state.map((s) => ({field: s.id, sort: s.desc ? "desc" : "asc"}));
}

/**
 * Holds the in-progress edit in its own state so a keystroke re-renders one cell
 * instead of the whole table. BillingPage's editable table is `showFullContent`
 * (every row on one page, unvirtualized) with ~35 columns, several rendering a
 * Tooltip or Chip — with the draft value in table-level state every keystroke
 * re-rendered all of them and re-ran the sort.
 *
 * Declared at module scope on purpose: a component defined inside LMTable would be
 * a new type on every render, so React would unmount the editor and drop the draft.
 */
function EditCell(props: {
  initial: string;
  isNumber: boolean;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = React.useState(props.initial);

  return (
    <TextField
      value={value}
      type={props.isNumber ? "number" : "text"}
      size="small"
      autoFocus
      variant="standard"
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => props.onCommit(value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") props.onCommit(value);
        if (e.key === "Escape") props.onCancel();
      }}
      onClick={(e) => e.stopPropagation()}
      // Without this the dblclick bubbles to the cell, re-enters startEdit and
      // resets the input to the original value mid-edit.
      onDoubleClick={(e) => e.stopPropagation()}
      sx={{width: "100%"}}
    />
  );
}

export default function LMTable<R extends Record<string, any> = any>(props: LMTableProps<R>) {
  const {t} = useTranslation();

  const getRowId = props.getRowId ?? ((row: R) => String(row.id ?? ""));
  const selectionMode: SelectionMode =
    props.selectionMode ?? (props.selectionHandler ? "single" : "none");
  const hasSelection = selectionMode !== "none";

  // ── Columns (translate headers once) ──
  const columns: LMColumn<R>[] = React.useMemo(
    () => props.columns.map((c) => ({...c, headerName: c.headerName ? t(c.headerName) : c.headerName})),
    [props.columns, t]
  );

  const cellValue = React.useCallback(
    (col: LMColumn<R>, row: R) => (col.valueGetter ? col.valueGetter(undefined, row) : row[col.field]),
    []
  );

  // ── Sorting (controlled when the caller supplies *both* halves) ──
  // Keying on the callback alone pinned the sort to EMPTY_SORT for a caller that
  // passed onSortModelChange without options.sortModel. TableFactory.createTable
  // always passes the callback, so any table that forgot the model would silently
  // never sort, with nothing to show for it.
  const controlledSort = !!props.onSortModelChange && props.options?.sortModel !== undefined;
  const [internalSort, setInternalSort] = React.useState<SortModel>(props.options?.sortModel ?? EMPTY_SORT);
  const sortModel = controlledSort ? props.options?.sortModel ?? EMPTY_SORT : internalSort;

  const handleSortingChange = (updater: SortingState | ((old: SortingState) => SortingState)) => {
    const current = toSorting(sortModel);
    const next = typeof updater === "function" ? updater(current) : updater;
    const nextModel = fromSorting(next);
    if (!controlledSort) setInternalSort(nextModel);
    props.onSortModelChange?.(nextModel);
  };

  const tanColumns = React.useMemo<ColumnDef<LMTableFeatures, R>[]>(
    () =>
      columns.map((col) => ({
        id: col.field,
        accessorFn: (row) => cellValue(col, row),
        enableSorting: col.sortable !== false,
        // Undefined cells are the comparator's business, not the table's: left at
        // its default the table would order them itself, before `sortFn` ever
        // runs, and the pre-slice sort in `sortBySortModel` would page rows in an
        // order the table then contradicts.
        sortUndefined: false,
        // A column may supply its own comparator (e.g. rank statuses by severity
        // rather than alphabetically); otherwise fall back to the shared default.
        // `sortingFn` was renamed to `sortFn` in react-table v9.
        sortFn: (rowA, rowB, columnId) =>
          col.sortComparator
            ? col.sortComparator(rowA.getValue(columnId), rowB.getValue(columnId))
            : compareValues(rowA.getValue(columnId), rowB.getValue(columnId)),
      })),
    [columns, cellValue]
  );

  // 'server' pagination: the parent supplies one already-paged slice of rows, so
  // LMTable must not re-slice it locally.
  const paginationMode: "client" | "server" =
    props.paginationMode ?? (props.rowCount != null ? "server" : "client");
  const serverMode = paginationMode === "server";

  // Sorting is independent of pagination — the old DataGrid sorted client-side by
  // default regardless of paginationMode. LMTable sorts the rows it is given in
  // the browser unless a caller opts into 'server' sorting, i.e. it re-fetches
  // ordered data when the sort model changes (only the audit logs do this).
  const sortingMode: "client" | "server" = props.options?.sortingMode ?? "client";

  // Must be referentially stable: react-table memoizes getSortedRowModel on the
  // identity of `state.sorting`, so building this inline re-sorted every row on
  // every render and invalidated every memo below.
  const tableState = React.useMemo(() => ({sorting: toSorting(sortModel)}), [sortModel]);

  const table = useTable<LMTableFeatures, R>({
    features: tableFeaturesConfig,
    data: props.rows,
    columns: tanColumns,
    state: tableState,
    onSortingChange: handleSortingChange,
    getRowId: (row) => getRowId(row),
    // getCoreRowModel is automatic in v9 (no longer a table option). Pagination
    // is handled entirely below (this component never registers a pagination
    // row model or calls a pagination table method), so there is no `manualPagination`
    // equivalent to set here anymore.
    // Only skip react-table's client sort when the caller sorts server-side; its
    // JS comparators would otherwise fight the server's collation.
    manualSorting: sortingMode === "server",
  });

  // getSortedRowModel() is internally memoized by react-table, but `.map` returns
  // a fresh array each render — memoize so the displayRows/apiRef consumers below
  // aren't invalidated on every render.
  const sortedRowModel = table.getSortedRowModel();
  const sortedRows: R[] = React.useMemo(
    () => sortedRowModel.rows.map((r) => r.original),
    [sortedRowModel]
  );

  // ── Pagination ──
  const controlledPagination = !!props.paginationModel;
  const [internalPagination, setInternalPagination] = React.useState<PaginationModel>({
    page: 0,
    pageSize: props.pageSizeOptions?.[0] ?? DEFAULT_PAGE_SIZE_OPTIONS[0],
  });
  const pagination = controlledPagination ? props.paginationModel! : internalPagination;

  const setPagination = (next: PaginationModel) => {
    if (!controlledPagination) setInternalPagination(next);
    props.onPaginationModelChange?.(next);
  };

  const showAll = props.showFullContent === true || pagination.pageSize === ALL_ROWS;

  const totalCount = serverMode ? props.rowCount ?? sortedRows.length : sortedRows.length;

  // Last valid page index. Used to clamp both what we render and the stored page,
  // so a shrinking row set (e.g. a narrowed search) can't strand the user on a
  // now-empty page — the old DataGrid clamped in *both* pagination modes. In
  // server mode we only hold one slice locally, so the clamp comes from rowCount.
  const lastPage =
    showAll || pagination.pageSize <= 0
      ? 0
      : Math.max(0, Math.ceil(totalCount / pagination.pageSize) - 1);

  // Page index used for rendering *and* for the pager below. `pagination.page` can
  // be out of range for a render — a narrowed search shrinks the row set before the
  // clamping effect below has run — and handing that straight to TablePagination
  // made it announce a range that doesn't exist ("76–90 of 3") beside the
  // empty-state row. Server mode without a rowCount is the one case we can't clamp:
  // totalCount is then just the size of the single slice we hold, which says nothing
  // about how many pages the parent has.
  let safePage = pagination.page;
  if (showAll) safePage = 0;
  else if (!(serverMode && props.rowCount == null)) safePage = Math.min(pagination.page, lastPage);

  // Rows actually rendered.
  const displayRows: R[] = React.useMemo(() => {
    if (showAll || serverMode) return sortedRows;
    // client pagination → slice locally
    const start = safePage * pagination.pageSize;
    return sortedRows.slice(start, start + pagination.pageSize);
  }, [showAll, serverMode, sortedRows, safePage, pagination.pageSize]);

  // Persist the clamp so the pager doesn't report an out-of-range page and a
  // server-mode parent re-fetches a page that actually exists. Skipped while a
  // server-mode caller hasn't told us a total yet — we'd have nothing to clamp to.
  React.useEffect(() => {
    if (showAll) return;
    if (serverMode && props.rowCount == null) return;
    if (pagination.page > lastPage) setPagination({page: lastPage, pageSize: pagination.pageSize});
    // setPagination is rebuilt each render; every value it reads is in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAll, serverMode, props.rowCount, lastPage, pagination.page, pagination.pageSize]);

  // ── Selection (single-select) ──
  const selected = React.useMemo(() => new Set(props.selectionModel ?? []), [props.selectionModel]);

  const toggleRow = (id: string) => {
    if (!props.selectionHandler) return;
    // Radio-style: re-selecting the current row clears it, any other row replaces
    // the selection. The single-selection cap is enforced here, so callers can
    // store the model verbatim instead of trimming it themselves.
    props.selectionHandler(selected.has(id) && selected.size === 1 ? [] : [id]);
  };

  // ── Inline editing ──
  // Only which cell is open lives here — the draft value is EditCell's own state, so
  // typing doesn't re-render the table. This still flips twice per edit (open and
  // close), which is what the old DataGrid did too.
  const [editing, setEditing] = React.useState<{rowId: string; field: string} | null>(null);

  // Mirrors `editing` for the commit path. onBlur fires while the input is being
  // unmounted — after Enter or Escape has already closed the editor — and by then
  // the handler's captured `editing` is stale. Reading the ref instead means a
  // closed editor commits exactly once, and Escape cancels instead of saving.
  const editingRef = React.useRef<{rowId: string; field: string} | null>(null);

  const openEdit = (cell: {rowId: string; field: string}) => {
    editingRef.current = cell;
    setEditing(cell);
  };

  const closeEdit = () => {
    editingRef.current = null;
    setEditing(null);
  };

  const startEdit = (rowId: string, field: string) => {
    openEdit({rowId, field});
  };

  // Cells with a save in flight. Enter causes a blur, so without this the same
  // edit would be written twice — once per handler.
  const commitsInFlight = React.useRef<Set<string>>(new Set());

  const commitEdit = async (row: R, col: LMColumn<R>, editValue: string) => {
    const cell = editingRef.current;
    if (!cell || !props.processRowUpdate) {
      closeEdit();
      return;
    }
    const cellKey = `${cell.rowId}:${cell.field}`;
    if (commitsInFlight.current.has(cellKey)) return;

    const isNumber = col.type === "number";
    // A blank numeric input is "no change", not 0 — Number("") is 0, which would
    // silently overwrite the value (e.g. zero out a billing price). Only a real
    // number commits.
    if (isNumber && (editValue.trim() === "" || Number.isNaN(Number(editValue)))) {
      closeEdit();
      return;
    }
    const parsed = isNumber ? Number(editValue) : editValue;
    const oldValue = row[col.field];
    if (parsed === oldValue) {
      closeEdit();
      return;
    }
    const newRow = {...row, [col.field]: parsed} as R;
    commitsInFlight.current.add(cellKey);
    try {
      await props.processRowUpdate(newRow, row);
      // Only close if this cell is still the one being edited — the save is a full
      // round trip, and the user may have opened a different cell in the meantime.
      const open = editingRef.current;
      if (open && `${open.rowId}:${open.field}` === cellKey) closeEdit();
    } catch (error) {
      // Don't swallow the rejection as an unhandled promise error. The cell is left
      // open so the user's edit isn't lost and can be retried.
      console.error("LMTable: processRowUpdate failed", error);
      props.onProcessRowUpdateError?.(error);
    } finally {
      commitsInFlight.current.delete(cellKey);
    }
  };

  // ── Imperative API for CSV export etc. ──
  React.useEffect(() => {
    const apiRef = props.apiRef;
    if (!apiRef) return;
    apiRef.current = {
      getSortedRows: () => sortedRows,
      getVisibleColumns: () => columns,
    };
    // Clear on unmount, or a page that swaps the table for a spinner/error keeps
    // exporting the rows this table held when it disappeared.
    return () => {
      apiRef.current = null;
    };
  }, [props.apiRef, sortedRows, columns]);

  // ── Rendering helpers ──
  // Numeric columns right-align unless the column says otherwise, matching the old
  // DataGrid's GRID_NUMERIC_COL_DEF (which also set headerAlign).
  const alignFor = (col: LMColumn<R>, part: "cell" | "header"): Align => {
    const explicit = part === "header" ? col.headerAlign ?? col.align : col.align;
    if (explicit) return explicit;
    return col.type === "number" ? "right" : "left";
  };

  const renderCell = (col: LMColumn<R>, row: R) => {
    const value = cellValue(col, row);
    const rowId = getRowId(row);
    const isEditingCell = editing?.rowId === rowId && editing?.field === col.field;

    if (isEditingCell) {
      const raw = row[col.field];
      return (
        <EditCell
          initial={raw == null ? "" : String(raw)}
          isNumber={col.type === "number"}
          onCommit={(value) => commitEdit(row, col, value)}
          onCancel={closeEdit}
        />
      );
    }

    if (col.renderCell) {
      const params: CellParams<R> = {value, row, field: col.field};
      return col.renderCell(params);
    }
    return value == null ? "" : String(value);
  };

  // The pager stays visible even when the user picks "All" from the dropdown, so
  // they can switch back. Only an explicit showFullContent (or hideFooter) removes it.
  const showPager = props.showFullContent !== true && !props.hideFooter;

  // DataGrid's `flex` was a proportional share of the space *left over* after the
  // fixed-`width` columns, not a pixel count. Distribute it over that remainder:
  // taking a share of the full 100% over-constrained any table whose flex columns
  // already sum to the whole width alongside a fixed column (e.g.
  // AssignedCustomersTable: flex columns totalling 100% plus a 64px column).
  // The selection checkbox column has no declared width, so it isn't subtracted.
  const {totalFlex, fixedWidthPx} = React.useMemo(() => {
    let flex = 0;
    let fixed = 0;
    for (const c of columns) {
      if (c.width) fixed += c.width;
      else flex += c.flex ?? 0;
    }
    return {totalFlex: flex, fixedWidthPx: fixed};
  }, [columns]);
  const pageSizeOptions = props.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS;
  // "All" is only safe in client mode, where LMTable owns the slicing. In server
  // mode the parent controls paging and cannot handle an unbounded page size.
  const rowsPerPageOptions =
    paginationMode === "client"
      ? [...pageSizeOptions, {label: t("common.all"), value: ALL_ROWS}]
      : pageSizeOptions;

  return (
    <Box sx={{width: "100%"}}>
      {props.loading && <LinearProgress/>}
      <TableContainer sx={{maxHeight: props.maxHeight ?? "70vh"}}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {hasSelection && <TableCell padding="checkbox"/>}
              {columns.map((col) => {
                const active = sortModel.find((s) => s.field === col.field);
                const sortable = col.sortable !== false;
                return (
                  <TableCell
                    key={col.field}
                    align={alignFor(col, "header")}
                    sortDirection={active ? active.sort ?? false : false}
                    style={{
                      width:
                        col.width ??
                        (col.flex && totalFlex > 0
                          ? `calc((100% - ${fixedWidthPx}px) * ${col.flex / totalFlex})`
                          : undefined),
                      minWidth: col.width ?? col.minWidth ?? 100,
                      fontWeight: 600,
                    }}
                  >
                    {sortable ? (
                      <TableSortLabel
                        active={!!active}
                        direction={active?.sort ?? "asc"}
                        onClick={() => {
                          const dir = !active ? "asc" : active.sort === "asc" ? "desc" : null;
                          const next: SortModel = dir ? [{field: col.field, sort: dir}] : [];
                          if (!controlledSort) setInternalSort(next);
                          props.onSortModelChange?.(next);
                        }}
                      >
                        {col.headerName}
                      </TableSortLabel>
                    ) : (
                      col.headerName
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {displayRows.length === 0 && !props.loading ? (
              <TableRow>
                <TableCell colSpan={columns.length + (hasSelection ? 1 : 0)} align="center">
                  {props.emptyMessage ?? t("common.noRows")}
                </TableCell>
              </TableRow>
            ) : (
              displayRows.map((row) => {
                const rowId = getRowId(row);
                const isSelected = selected.has(rowId);
                // Clicking anywhere on the row selects it (restores the old DataGrid
                // behaviour). A dedicated onRowClick (e.g. navigation) takes precedence.
                // The checkbox stops propagation, so it won't double-toggle.
                const handleRowClick = props.onRowClick
                  ? () => props.onRowClick!(row)
                  : hasSelection
                    ? () => toggleRow(rowId)
                    : undefined;
                return (
                  <TableRow
                    key={rowId}
                    hover
                    selected={isSelected}
                    onClick={handleRowClick}
                    sx={handleRowClick ? {cursor: "pointer"} : undefined}
                  >
                    {hasSelection && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          slotProps={{input: {"aria-label": t("common.selectRow")}}}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleRow(rowId)}
                        />
                      </TableCell>
                    )}
                    {columns.map((col) => {
                      const editable = col.editable && !!props.processRowUpdate;
                      return (
                        <TableCell
                          key={col.field}
                          align={alignFor(col, "cell")}
                          onDoubleClick={editable ? () => startEdit(rowId, col.field) : undefined}
                          sx={editable ? {bgcolor: "action.hover", cursor: "text"} : undefined}
                        >
                          {renderCell(col, row)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>

          {props.options?.summaryFooter && (
            <TableFooter>
              <ICNSummaryFooter
                columns={columns}
                tableData={sortedRows as any[]}
                hasSelection={hasSelection}
              />
            </TableFooter>
          )}
        </Table>
      </TableContainer>

      {showPager && (
        <TablePagination
          component="div"
          count={totalCount}
          page={safePage}
          rowsPerPage={pagination.pageSize}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={(_e, newPage) => setPagination({...pagination, page: newPage})}
          onRowsPerPageChange={(e) => {
            const value = parseInt(e.target.value, 10);
            setPagination({page: 0, pageSize: value});
          }}
        />
      )}
    </Box>
  );
}
